import { appCurrency } from "../utils/currency";
import { evaluateExpression } from "../utils/evaluateExpression";
import Decimal from "decimal.js";
import { parseDecimalAmount } from "../utils/parse_decimal_amount";

export class CurrencyInputElement extends HTMLElement {
  static formAssociated = true;

  private static counter = 0;
  private internals: ElementInternals;
  private input: HTMLInputElement;
  private numericValue: Decimal;
  private _initialising = false;

  constructor() {
    super();

    this.internals = this.attachInternals();

    this.input = document.createElement("input");
    this.input.type = "text";

    this.attachInputEventHandlers(this.input);
  }

  connectedCallback() {
    this.connectInput();

    // Generate unique ID for the input
    const originalId = this.getAttribute("id");
    const originalName = this.getAttribute("name");
    const baseId = originalId || originalName;
    if (this.input.id === "") {
      this.input.id = `currency-input-${baseId}-${CurrencyInputElement.counter++}`;
    }

    // Forward attributes to the internal input, except for name, so it does not submit values.
    // We collect names to remove first to avoid mutating the attributes list while iterating.
    const toRemove: string[] = [];
    Array.from(this.attributes).forEach((attr) => {
      switch (attr.name) {
        case "id":
        case "name":
        case "class":
        case "decimal-separators":
          break;
        case "data-input-class":
          this.input.setAttribute("class", attr.value);
          break;
        default:
          this.input.setAttribute(attr.name, attr.value);
          toRemove.push(attr.name);
      }
    });
    // Remove after iteration. Use a flag so attributeChangedCallback ignores
    // these removals — the values have already been applied to the inner input.
    this._initialising = true;
    toRemove.forEach((name) => this.removeAttribute(name));
    this._initialising = false;

    // Set initial value
    this.evaluateAndFormat();
  }

  private connectInput() {
    const existingInput = Array.from(this.children).find(
      (child): child is HTMLInputElement => child instanceof HTMLInputElement
    );

    if (existingInput !== undefined && existingInput !== this.input) {
      this.input = existingInput;
      this.attachInputEventHandlers(this.input);
    }

    this.replaceChildren(this.input);
  }

  private attachInputEventHandlers(input: HTMLInputElement) {
    input.addEventListener("focus", this.handleFocus.bind(this));
    input.addEventListener("blur", this.handleBlur.bind(this));
    input.addEventListener("change", this.handleChange.bind(this));
  }

  static get observedAttributes() {
    return ["value", "disabled", "required", "placeholder"];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (this._initialising) return;
    switch (name) {
      case "value":
        if (newValue !== null) {
          this.value = newValue;
        }
        break;
      case "disabled":
        if (newValue !== null) {
          this.input.disabled = true;
        } else {
          this.input.disabled = false;
        }
        break;
      case "required":
        if (newValue !== null) {
          this.input.required = true;
        } else {
          this.input.required = false;
        }
        break;
      case "placeholder":
        if (newValue !== null) {
          this.input.placeholder = newValue;
        } else {
          this.input.placeholder = "";
        }
        break;
    }
  }

  private get decimalOnly() {
    return this.getAttribute("decimal-separators") === "both";
  }

  private handleFocus(event: FocusEvent) {
    // Quick owns empty/new-row drafts; focusing must not edit their value.
    if (this.decimalOnly) return;
    if (appCurrency(this.input.value).value === 0) {
      this.input.value = "";
    }
  }

  private handleBlur(event: FocusEvent) {
    if (this.decimalOnly) return;
    if (this.input.value === "") {
      this.input.value = appCurrency(0).format();
    }
  }

  private handleChange(event: Event) {
    this.evaluateAndFormat();

    this.dispatchEvent(
      new Event("change", {
        bubbles: true,
        composed: true,
      })
    );
  }

  private evaluateAndFormat() {
    // Opt-in decimal editing keeps unfinished input intact and never routes it
    // through the permissive formula parser. Other editors keep their contract.
    if (this.decimalOnly) {
      const amount = parseDecimalAmount(this.input.value);
      if (amount) {
        this.setValue(amount);
      } else {
        this.internals.setValidity({ badInput: true }, "Enter a complete signed amount.", this.input);
        this.input.addEventListener("input", () => this.internals.setValidity({}), { once: true });
      }
      return;
    }
    if (this.input.value.trim() === "") {
      this.setValue(new Decimal(0));
      return;
    }

    try {
      this.setValue(new Decimal(this.input.value));
      return;
    } catch {
      // Try formula evaluation
    }

    // Replace dash [U+2212 -> −] with minus [U+002d -> -]
    const minusValue = this.input.value.replaceAll("\u2212", "\u002d");

    // Only allow basic arithmetic operations
    const safeValue = minusValue.replace(/[^0-9+\-*/.,()\s]/g, "");
    if (safeValue === minusValue) {
      try {
        this.setValue(evaluateExpression(safeValue));
        return;
      } catch (e) {
        console.info(`Failed to evaluate expression: ${this.input.value}`);
      }
    }

    // Invalid input
    this.internals.setValidity(
      { badInput: true },
      `Invalid expression: ${this.input.value}. Please enter a valid mathematical expression or number.`,
      this.input as HTMLInputElement
    );
    this.input.addEventListener(
      "input",
      () => {
        this.internals.setValidity({});
      },
      {
        once: true,
      }
    );
    this.internals.reportValidity();
  }

  // Public API: expose strings to keep it consistent with native input elements
  get value(): string {
    return this.numericValue.toString();
  }

  set value(val: string) {
    if (this.decimalOnly) {
      // Programmatic values are canonical decimals, not user-entered separators.
      this.setValue(new Decimal(val));
      return;
    }
    this.input.value = val;
    this.evaluateAndFormat();
  }

  setValue(val: Decimal) {
    this.numericValue = val;
    this.input.value = appCurrency(this.numericValue.toNumber()).format();
    this.internals.setFormValue(this.numericValue.toString());
    this.internals.setValidity({});
  }
}

// Register the custom element
customElements.define("currency-input", CurrencyInputElement);
